import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import { getDocument, putDocument } from "net/contracts";
import { IDocument } from "dataModels/contract";
import { Container, Col, Row } from "react-grid-system";
import "views/contractView/styles/newElementFormStyles.css";
import DSFileInput from "components/DSFileInput";
import { FileStatusEnum, IFileUpload } from "dataModels/fileInput";
import { DocumentType, EntityType, RoutePath, DATE_FORMAT, DsLayout, ResponseStatus, ResponseCode } from "shared/constants";
import { InputAttach, DatePicker, Button } from "@ds/ds-snam/react";
import { useNavigate } from "react-router-dom";
import { showNotification, hideNotification } from "state/reducer/notificationSlice";
import { INotificationAction, NotificationsStatusType } from "dataModels/stateModels/notification";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { RootState } from "state/appStore";



const EditDocument: React.FC = (): JSX.Element => {
    const {t} = useTranslation(['contract', 'app']);
    //eslint-disable-next-line
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();
    const entityId: string | null = (searchParams as URLSearchParams).get("entityId");
    const entityType: EntityType | null = (searchParams as URLSearchParams).get("entityType") as EntityType;
    const documentType: string | null = (searchParams as URLSearchParams).get("documentType") as DocumentType;
    const optionalParam = (searchParams as URLSearchParams).get("transporterId");
    
    const layoutMode = useSelector((state:RootState)=>{
        return state.dsLayout.mode;
    })
    const [document, setDocument] = useState<IDocument | undefined>();

    const navigate = useNavigate();

    const loadDocument = useCallback(() => {
        dispatch(requestPending());
        getDocument(entityId!, entityType!, documentType!)
            .then((data: any) => {
                if(data.status===ResponseStatus.ERROR|| data.code===ResponseCode.UNPROCESSABLE_CONTENT){
                    // qui controllo il document type, perchè potrei non trovarlo in quanto non è nel blobstorage erchè è un documento facoltativo
                    // in quel caso si metto un documento vuoto, ma devo mettere l'expire in modo corretto, in base a se quel documento ha l'expire o meno
                    /**
                    Documenti facoltativi nell'applicativo:
                    fiscalCode -> expire true
                    gaDrawing -> expire false
                    testCertificate -> expire true
                    flameArrest -> expire false

                    Documenti senza expire
                    personalData (driver)
                    securityTemplate (carrier)
                    registrationDocument (vehicle -> Tractor, Semi-trailer,Flat-bed)
                    gaDrawing (vehicle -> Tractor,Semi-trailer,IsoContainer,FlatBed)
                    flameArrest (vehicle -> Tractor)
                    pidPfd (vehicle -> Semi-trailer,IsoContainer)
                    procedures (vehicle -> Semi-trailer,IsoContainer)
                    */
                   if(
                    documentType === DocumentType.GA_DRAWING || 
                    documentType === DocumentType.FLAME_ARREST ||
                    documentType === DocumentType.PERSONAL_DATA ||
                    documentType === DocumentType.SECURITY_TEMPLATE ||
                    documentType === DocumentType.REGISTRATION_DOCUMENT ||
                    documentType === DocumentType.PID ||
                    documentType === DocumentType.PROCEDURES 
                ){
                        setDocument({
                            type: "",
                            name: "",
                            size: 0,
                            content: "",
                            expire: null,
                            status: 0,
                        });
                   }else{
                        setDocument({
                            type: "",
                            name: "",
                            size: 0,
                            content: "",
                            expire: 0,
                            status: 0,
                        });
                   }
                }else{
                    setDocument(data);
                }
            })
            .catch((error) => {
                if(documentType === DocumentType.GA_DRAWING || 
                    documentType === DocumentType.FLAME_ARREST ||
                    documentType === DocumentType.PERSONAL_DATA ||
                    documentType === DocumentType.SECURITY_TEMPLATE ||
                    documentType === DocumentType.REGISTRATION_DOCUMENT ||
                    documentType === DocumentType.PID ||
                    documentType === DocumentType.PROCEDURES
                ){
                        setDocument({
                            type: "",
                            name: "",
                            size: 0,
                            content: "",
                            expire: null,
                            status: 0,
                        });
                   }else{
                        setDocument({
                            type: "",
                            name: "",
                            size: 0,
                            content: "",
                            expire: 0,
                            status: 0,
                        });
                   }
                console.log(error)
            })
            .finally(() => {
                dispatch(requestPristine())
            })
    }, [dispatch, documentType, entityId, entityType]);

    useEffect(() => {
        if (entityId && entityType && documentType) {
            loadDocument()
        }
    }, [loadDocument, entityId, entityType, documentType]);

    const getNavigationTarget = () => {
        if (entityType === EntityType.CONTRACT) {
            return RoutePath.CONTRACTS_CONTRACT_INFO;// + "/" + entityId;
        } else if (entityType === EntityType.TRANSPORTER) {
            return RoutePath.CONTRACTS_TRANSPORTATORS_INFO; // To be modified
        } else if(entityType === EntityType.DRIVER ){
            return RoutePath.CONTRACTS_DRIVER_INFO;
        } else if(entityType === EntityType.VEHICLE){
            return RoutePath.CONTRACTS_VEHICLE_INFO;
        }

    }
    const saveData = useCallback(() => {
        let popUpAction: INotificationAction;
        dispatch(requestPending());
        const translationEntity = entityType===EntityType.DRIVER?"drivers":(entityType===EntityType.TRANSPORTERDELEGATION?"transporter":entityType);
        
        
        putDocument(entityId!, entityType!, document!)
            .then(response => {
                const popUpTitle:string = response.data.status===ResponseStatus.ERROR ? 'Error':(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT? "Warning": t(`${translationEntity}.documentEdit.successToast.title`));
                const popUpDescription:string = response.data.status===ResponseStatus.ERROR||response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?response.data.message:t(`${translationEntity}.documentEdit.successToast.subTitle`);
                const popUpStatus:NotificationsStatusType = response.data.status===ResponseStatus.ERROR?"error":(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?"warning":"confirm");
                                
                popUpAction = {
                    show: true,
                    title: popUpTitle,
                    description: popUpDescription,
                    status: popUpStatus,
                    onClose: (event: Event) => {
                        dispatch(hideNotification());
                    }
                };
                dispatch(showNotification(popUpAction));
                //navigate(getNavigationTarget()!);
                if(entityType===EntityType.TRANSPORTERDELEGATION){
                    if (optionalParam) {
                        //console.log("Parametro facoltativo:", optionalParam);
                        navigate(RoutePath.CONTRACTS_TRANSPORTATORS_INFO+"/"+optionalParam);
                    }
                    
                }else{
                    navigate(getNavigationTarget()!+"/"+entityId);
                }
                
            }).catch((error) => {
                console.log(error)
            })
            .finally(() => {
                dispatch(requestPristine())
            });
            //eslint-disable-next-line
    }, [entityId, entityType, document, dispatch, navigate, t]);

    const getTitle = ()=>{
        return t((entityType===EntityType.DRIVER?"drivers":(entityType===EntityType.TRANSPORTERDELEGATION?"transporter":entityType))+".documentEdit.title");
    }
    const getSubtitle = () => {
        let retVal: string = ""
        if (entityType === EntityType.CONTRACT) {
            retVal = t('contract.documentEdit.subTitle');//"Inserisci il documento";
        } else if (entityType === EntityType.TRANSPORTER||entityType === EntityType.TRANSPORTERDELEGATION) {
            retVal = t('transporter.documentEdit.subTitle.'+documentType);
        } else if(entityType === EntityType.DRIVER){
            retVal = t("drivers.documentEdit.subTitle.prefix")+" "+t('drivers.documentEdit.subTitle.'+documentType);
        }else if(entityType === EntityType.VEHICLE){
            retVal = t("vehicle.documentEdit.subTitle.prefix")+" "+t('vehicle.documentEdit.subTitle.'+documentType);
        }
        return retVal;
    }
    const getFileInputTitle = () => {
        let retVal: string = "";
        if (entityType === EntityType.CONTRACT) {
            retVal = t('contract.documentEdit.fileInputTitle')//"Documentazione contrattuale";
        } else if (entityType === EntityType.TRANSPORTER||entityType === EntityType.TRANSPORTERDELEGATION) {
            retVal = t('transporter.documentEdit.fileInputTitle.'+documentType);
        } else if(entityType === EntityType.DRIVER){
            retVal = t('drivers.documentEdit.subTitle.'+documentType);
        } else if(entityType === EntityType.VEHICLE){
            retVal = t('vehicle.documentEdit.subTitle.'+documentType);
        }
        return retVal;
    }

    return <>
        <Container fluid>
            <Row>
                <Col>
                    <p className="pageTitle">
                        {getTitle()}
                    </p>
                </Col>
            </Row>
            <div className="listTableContainerGrey">
            <Row>
                <Col>
                    <p className="formSubTitle">
                        {getSubtitle()}
                    </p>
                </Col>
            </Row>

            {document &&<Row>
                <Col>
                    <div className="fileInputWrapper">
                        <DSFileInput
                            title={getFileInputTitle()}//"Documentazione contrattuale"
                            //inputLabel={document ? "Caricamento file completato. Trascina o carica un'altro file per sostituirlo" : "Trascina il file per caricarlo"}
                            inputLabel={document&&document.size!==0 ? t('dsComponents.fileInput.uploadedStatus.title', {ns:'app'}) : t('dsComponents.fileInput.defaultStatus.title', {ns:'app'})}
                            buttonText={t('dsComponents.fileInput.defaultStatus.subTitle', {ns:'app'})}//"Oppure importa file"/
                            id="doc"
                            accept="application/pdf, image/jpg, image/jpeg"
                            maxSize={10000000}
                            defaultStatus={document&&document.size!==0 ? FileStatusEnum.UPLOADED : FileStatusEnum.DEFAULT}
                            onFileUpload={(fileUpload: IFileUpload, id: string) => {
                                //console.log("on file upload", fileUpload, id);
                                let base64String = btoa(fileUpload.content);
                                const documentObj: IDocument = {
                                    type: documentType,
                                    name: fileUpload.name,
                                    size: fileUpload.size,
                                    content: base64String,
                                    expire: document?.expire?document?.expire:null,
                                    //status:null
                                }
                                setDocument(documentObj);
                            }}
                        />
                    </div>
                </Col>
            </Row>}
            
            {document && <Row>
                <div className="uploadedFileWrapper" style={{paddingLeft:'16px'}}>
                    <InputAttach
                        attachName={document.name}
                        firstButtonDisabled={false}
                        secondButtonDisabled={false}
                        inputLabel={t('contract.contractForm.sections.fileInput.attachTitle')}//"File caricato"

                    />
                </div>
                {
                (
                    /**
                     * 
                     * SE NON VOGLIO VEDERE IL CAMPO DATA SCADENZA INSERISCO QUESTO CONTROLLO
                     */
                    documentType !== DocumentType.TRUCK_INSPECTION && //??
                    documentType !== DocumentType.GA_DRAWING && 
                    documentType !== DocumentType.FLAME_ARREST &&
                    documentType !== DocumentType.PERSONAL_DATA &&
                    documentType !== DocumentType.SECURITY_TEMPLATE &&
                    documentType !== DocumentType.REGISTRATION_DOCUMENT &&
                    documentType !== DocumentType.PID &&
                    documentType !== DocumentType.PROCEDURES 
                ) && 
                <Col xs={12} md={3}>
                    <div className="expirationInputWrapper" style={{width:'196px'}}>
                        <DatePicker
                            id="scadenzaDoc"
                            datePickerTitle={t('transporter.transporterForm.sections.fileInput.datePickerTitle')}//}"Scadenza*"
                            isRangePicker={false}
                            datePickerVariant="full-date"
                            datePickerPlaceholder=""
                            startingYear={new Date().getFullYear()}
                            //endingYear={2030}
                            defaultDate={moment.unix(document.expire!).toDate()}
                            isDisabled={false}
                            datePickerStatus="default"
                            confirmButtonLabel={t('general.buttons.confirm', {ns:'app'})}//"Conferma"
                            hasCloseButton={false}
                            zIndex={999}
                            isReadonly={false}
                            isPanelTop={true}
                            disabledDates={{ lastDisabledDate: new Date(Date.now() - 86400000) }} 
                            onDateSelection={(event: Event) => {
                                const date: string = (event as CustomEvent).detail.dateSelection;
                                document.expire = moment.utc(date, DATE_FORMAT).unix();
                            }}
                        ></DatePicker>
                    </div>
                </Col>}
            </Row>}
            </div>
            <Row>
                <Col>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.8rem" }}>
                        <Button
                            buttonType="button"
                            buttonUIVariant="primary"
                            buttonSize="medium"
                            buttonLabel={t('general.buttons.confirm', { ns: 'app' })}//"Conferma"
                            buttonIcon=""
                            isDisabled={document?.size!==0?false:true}
                            onClickEvent={(event: Event) => {
                                //console.log(document)
                                saveData();
                            }}
                        />
                    </div>
                </Col>
            </Row>
        </Container>
    </>
}

export default EditDocument;